import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';

import { NbTokenService } from '@nebular/auth';
import { NbToastrService, NbDialogService } from '@nebular/theme';
import { Assessment, AssessmentData } from '../../../../@core/interfaces/common/CalorieFriend/assessment';
import { Utility } from '../../../../@core/utils/utility';
import { AssessmentDescriptiveValues } from '../../PatientSheetView/Assessments/DescriptiveValues';
import { THIS_EXPR } from '@angular/compiler/src/output/output_ast';
import { PatientsService } from 'app/@core/backend/common/services/CalorieFriend/patients.service';
import { PatientData } from 'app/@core/interfaces/common/CalorieFriend/patients';
import { AssessmentSettingService } from 'app/@core/backend/common/services/CalorieFriend/assessment-settings.service';
import { AssessmentProgressImageType } from './../../PatientSheetView/Assessments/edit-assessment.component';
import { defaultFormValues } from 'app/@core/utils/defaultFormValues';
import { FormlyFormOptions } from '@ngx-formly/core';

/**
 * The Component for front-end users (patients) to input assessment data
 */
@Component({
  selector: 'app-add-assessment',
  templateUrl: './assessment.component.html',
  styleUrls: ['./assessment.component.scss'],
})
/** AddAssessment component*/
export class AssessmentInputComponent {

  AddAssessmentLabel = 'Input Assessment Data';
  UpdateAssessmentLabel = 'Update Assessment';
  assessmentProgressImageType: typeof AssessmentProgressImageType = AssessmentProgressImageType;
  AssessmentLabel = '';

  SaveAssessmentLabel = 'Save Assessment';

  MealPlanComplianceLabel = 'Meal Plan Compliance';
  EnergyLevelLabel = 'Energy Level';
  StressLevelLabel = 'Stress Level';
  SleepQualityLabel = 'Sleep Quality';

  WeightLabel = 'Weight';
  BodyFatLabel = 'Body Fat %';
  CommentLabel = 'Comment';
  StartDateLabel = 'Start Date';
  EndDateLabel = 'End Date';

  PreferredUnitLabel = 'Preferred Unit';

  BackLabel = 'Back';
  Created = 'Created';
  Updated = 'Updated';
  ProblemCeatingAssessment = 'A problem occurred submitting the assessment.';

  AssessmentGuid: string = null;
  PatientName: string = '';
  StartDate: any;
  EndDate: any;

  IsSelfCheckin : boolean = false;
  HideSaveButton = false;

  PatientId;
  formlyForm: any;
  form = new FormGroup({});
  model: any = { /* initial model */ };

  DescriptiveValues = AssessmentDescriptiveValues;
  isLoading = false;
  isImageProgressUpload: boolean = true;
  options: FormlyFormOptions = {
    formState: {
      customData: {
        isImperial: true,
        // Add more custom data as needed
      }
    }
  };

  protected readonly unsubscribe$ = new Subject<void>();

  /** Assessment ctor */
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private assessmentsService: AssessmentData,
    private tokenService: NbTokenService,
    private toasterService: NbToastrService,
    private dialogService: NbDialogService,
    private PlanViewPatientsService: PatientData,
    private assessmentSettingService: AssessmentSettingService
  ) {
  }

  ngOnInit(): void {

    const guid = this.route.snapshot.paramMap.get('guid');
    const notificationToken = this.route.snapshot.paramMap.get('NotificationToken');
    this.patientAndAssessmentSettings(notificationToken);
    if (guid !== null) {
      this.loadAssessmentData(guid, notificationToken);
      this.AssessmentLabel = this.UpdateAssessmentLabel;
    } else {
      this.AssessmentLabel = this.AddAssessmentLabel;
      this.IsSelfCheckin = true;
    }
  }

  /**
   * Function to convert the input data to the Assessment data type
   * @param value
   */
  ConvertToAssessment(value: any): Assessment {
    if (!value.ProgressImageUrl || value.ProgressImageUrl == 'null')
      value.ProgressImageUrl = '';

    const assessment: Assessment = value;

    return assessment;
  }

  Back() {

  }

  /**
   * Handler of Success event of submit
   */
  handleSuccessResponse() {
    if (this.AssessmentGuid === null)
      this.toasterService.success('', `Item ${this.Created}!`);
    else
      this.toasterService.success('', `Item ${this.Updated}!`);

    this.Back();
  }

  /**
   * Handler of Fail event of submit
   */
  handleWrongResponse() {
    this.toasterService.danger('', this.ProblemCeatingAssessment);
  }

  private patientAndAssessmentSettings(notificationToken): void {
    this.isLoading = true;
    this.PlanViewPatientsService.get(null, notificationToken)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((patient) => {
      this.options.formState.customData.isImperial = patient.isPreferredUnitImperial;
      console.log("Is imperial", patient.isPreferredUnitImperial);
      this.PatientName = patient.name;
      this.PatientId = patient.id;
      // TODO: add this patient.createdByUserId
      this.assessmentSettingService.getAssessmentSettingsByUserId(patient.createdByUserId)
      .pipe(finalize(() => {
        this.isLoading = false;
      })).subscribe((data) => {
        console.log(data);
        if(data) {
          this.isImageProgressUpload = data.uploadProgressImages;
          this.formlyForm = JSON.parse(data.customQuestion);
        } else {
          this.formlyForm = defaultFormValues;
        }
      });
    }, (error) => {
      this.isLoading = false;
    });
  }

  /**
   * Function to load the assessment data
   */
  loadAssessmentData(guid, notificationToken) {
    if (guid) {
        this.assessmentsService.getByNotification(guid, notificationToken)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((assessment) => {

          this.AssessmentGuid = assessment.guid;
          this.PatientName = assessment.patientName;

          this.StartDate = assessment.startDateString;
          this.EndDate = assessment.endDateString;
          let answersJson = assessment.answersJson?JSON.parse(assessment.answersJson): {};
          this.model = {
            id: assessment.id,
            guid: assessment.guid,
            height: assessment.height,
            weight: assessment.weight,
            bodyFat: assessment.bodyFat,
            mealPlanCompliance: assessment.mealPlanCompliance > 0 ? assessment.mealPlanCompliance : null,
            energyLevel: assessment.energyLevel > 0 ? assessment.energyLevel : null,
            stressLevel: assessment.stressLevel > 0 ? assessment.stressLevel : null,
            sleepQuality: assessment.sleepQuality > 0 ? assessment.sleepQuality : null,
            comment: assessment.comment ?? '',
            ...answersJson,
            progressImageUrl: assessment.progressImageUrl,
            progressImageLeftUrl: assessment.progressImageLeftUrl,
            progressImageRightUrl: assessment.progressImageRightUrl,
          }
          this.HideSaveButton = assessment.submittedByPatient;

          this.imgProgressItem = assessment.progressImageUrl;
          if (!this.imgProgressItem || this.imgProgressItem == '') {
            this.imgProgressItem = null;
          }

        });
    }
  }

  imgHover: boolean = false;
  imgProgressItem: any = null;

  /**
   * Event handler for uploading the image
   * @param event Event object
   */
  OnInputImageFileChanged(event, type: AssessmentProgressImageType) {
    if (event) {
      this.model['fileProgressImage' + type] = event;
      this.model['progressImage' + type + 'Url'] = null;
    }
    console.log(this.model);
  }

  onResetFoodImage(type: AssessmentProgressImageType) {
    this.model['fileProgressImage' + type] = null;
    this.model['progressImage' + type + 'Url'] = null;
  }
  submit() {
    let Assessment: Assessment = {
      id: this.model.id,
      guid: this.model.guid,
      height: this.model.height,
      weight: this.model.weight,
      bodyFat: this.model.bodyFat,
      mealPlanCompliance: this.model.mealPlanCompliance > 0 ? this.model.mealPlanCompliance : 0,
      energyLevel: this.model.energyLevel > 0 ? this.model.energyLevel : 0,
      stressLevel: this.model.stressLevel > 0 ? this.model.stressLevel : 0,
      sleepQuality: this.model.sleepQuality > 0 ? this.model.sleepQuality : 0,
      progressImageUrl: this.model.progressImageUrl,
      progressImageLeftUrl: this.model.progressImageLeftUrl,
      progressImageRightUrl: this.model.progressImageRightUrl,
      comment: this.model.comment ?? '',
      answersJson: null,
      patientName: this.PatientName,
      patientID: this.PatientId? this.PatientId : 0,
      submittedByPatient: false,
      startDate: this.StartDate,
      endDate: this.EndDate,
      startDateString: '',
      endDateString: '',
    }

    if(this.model.fileProgressImage) {
      Assessment.fileProgressImage = this.model.fileProgressImage;
      delete this.model.fileProgressImage;
    }
    if(this.model.fileProgressImageRight) {
      Assessment.fileProgressImageRight = this.model.fileProgressImageRight;
      delete this.model.fileProgressImageRight;
    }
    if(this.model.fileProgressImageLeft) {
      Assessment.fileProgressImageLeft = this.model.fileProgressImageLeft;
      delete this.model.fileProgressImageLeft;
    }

    // Add the answersJson field after the Assessment object has been constructed
    Assessment.answersJson = JSON.stringify(this.model);

    Assessment.guid = this.AssessmentGuid;
    Assessment.submittedByPatient = true;

    if (this.IsSelfCheckin) {
      Assessment.patientID = this.PatientId;
    }

    if (!Assessment.bodyFat) Assessment.bodyFat = 0;

    if (!Assessment.weight) Assessment.weight = 0;

    const observable = this.assessmentsService.submit(Assessment);
    this.isLoading = true;
    observable
      .pipe(takeUntil(this.unsubscribe$),
      finalize(() => this.isLoading = false))
      .subscribe(() => {
        this.handleSuccessResponse();

        if (this.IsSelfCheckin) this.HideSaveButton = true;
        this.model.fileProgressImage = Assessment.fileProgressImage;
        this.model.fileProgressImageLeft = Assessment.fileProgressImageLeft;
        this.model.fileProgressImageRight = Assessment.fileProgressImageRight;
      },
        err => {
          this.handleWrongResponse();
        },
      );
  }

  /**
   * Validator that validates the number with the ##.# pattern
   */
  validateNumber = Utility.validateNumber;
}
